import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { service } from '@ember/service'
import { use } from 'ember-resources'
import { t } from 'ember-intl'
import { keepLatest } from 'reactiveweb/keep-latest'
import { trackedFunction } from 'reactiveweb/function'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { SpellingStudentWordsTable } from 'teacher-ui-client/components/writing-legends/tables/spelling-student-words-table'
import { StudentWordsPrintable } from 'teacher-ui-client/components/writing-legends/spelling-student-printable'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import type Student from 'teacher-ui-client/models/student'
import { deserialize as deserializeSort } from '@blakeelearning/data-tables/utils/sorting'
import NoReportValue from 'teacher-ui-client/components/reporting/no-report-value'
import ContentTile from 'teacher-ui-client/components/tiles/content'
import BreadcrumbLinkTo from 'teacher-ui-client/components/base/breadcrumbs/link-to'
import BreadcrumbSeparator from 'teacher-ui-client/components/base/breadcrumbs/separator'
import { hash } from '@ember/helper'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { on } from '@ember/modifier'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { Product } from 'teacher-ui-client/utils/products'
import { toRequestScopeStudent, type BaseReport, type BaseReportMetaPaging } from 'teacher-ui-client/services/reporting'
import { array } from '@ember/helper'

interface Attempt {
  created_date: string
  is_correct: boolean
}

export interface SpellingStudentWordsData extends BaseReport {
  words: [
    {
      attempts: Attempt[]
      incorrect_word: string
      last_attempted_at: string
      likely_correct_words: string
      spelling_issue: string
    },
  ]
  student: {
    first_name: string
    grade_position: number
    id: number
    last_name: string
    school_class_names: string[]
    teacher_names: string[]
  }
  meta: BaseReportMetaPaging
}

interface Signature {
  Args: {
    uiScope: ModelFor<ReportingUiScopeRoute>['uiScope']
    period: ModelFor<ReportingUiScopeRoute>['period']
    students: Student[]
    pageNumber: number
    perPage: number
  }
}

export class SpellingStudentWords extends Component<Signature> {
  @queryParam('reporting/ui-scope/writing-legends/spelling') sort!: string

  @queryParam('reporting/ui-scope/writing-legends/spelling') student!: string

  @service reporting!: ReportingService

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  fetchData = trackedFunction(this, () => {
    const requestScope = toRequestScopeStudent(this.student)

    const url = this.reporting.buildGravityUrl({
      product: Product.WL,
      requestScope,
      reportName: 'spelling/words',
    })

    const query = this.reporting.buildQuery({
      sort: this.sort,
      page: {
        number: 1,
        size: 10,
      },
    })

    return this.reporting.fetchReport<SpellingStudentWordsData>(url, query)
  })

  get studentFullName() {
    return `${this.latestData?.student.first_name} ${this.latestData?.student.last_name}`
  }

  print() {
    window.print()
  }

  get words() {
    let correctWords = this.latestData?.words.map((spellingWord) => spellingWord.likely_correct_words)
    if (!correctWords) correctWords = []

    // if less than 10 words, add blank strings to have upto 10 entries on the print sheet
    while (correctWords.length < 10) {
      correctWords.push('')
    }

    return correctWords
  }

  @use
  latestData = keepLatest({
    value: () => this.fetchData.value,
    when: () => this.fetchData.isPending,
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    {{#if this.latestData}}
      <div class="print:hidden">
        <div class="py-2">
          <BreadcrumbLinkTo
            @route="reporting.ui-scope.writing-legends.spelling"
            @query={{hash student="" sort="-total_incorrect"}}
          >
            {{t "pages.wlSpelling.tabs.myStudents"}}
          </BreadcrumbLinkTo>
          <BreadcrumbSeparator />
          {{this.studentFullName}}
        </div>

        <h2 class="py-3 text-center">{{t "components.reporting.tables.writingLegends.spelling.studentReport"}}
          -
          {{this.studentFullName}}
        </h2>
      </div>
      <ContentTile class="print:hidden">
        <SpellingStudentWordsTable
          class="w-full"
          @isReloading={{this.fetchData.isPending}}
          @spellingStudentWords={{this.latestData}}
        />
      </ContentTile>

      <UiButton
        class="mx-auto mt-6 w-60 print:hidden"
        @appearance={{ButtonAppearance.regular}}
        {{on "click" this.print}}
      >
        <PhosphorIcon @icon="phosphor-printer-fill" class="mr-2" />
        {{t "pages.wlSpelling.studentWordsPrint.buttonLabel"}}
      </UiButton>

      <StudentWordsPrintable class="mt-4" @words={{this.words}} @studentName={{this.studentFullName}} />
    {{else}}
      <NoReportValue @reportStates={{array this.fetchData}} />
    {{/if}}
  </template>
}
